import { ANALYTICS_EVENTS, INPUT_AUTOCOMPLETE, schemas } from '@nandosaus/constants';
import { observer } from 'mobx-react';
import { useRootStore } from '@nandosaus/state-management';
import concat from 'lodash/concat';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import reduceRight from 'lodash/reduceRight';

import { A, P } from '../typography';
import { Alert } from '../alert';
import { analytics } from '../../analytics';
import { Box } from '../grid';
import { Form } from '../form';
import { FormikTextInput } from '../input';
import { logger } from '../../utils/logger';

dayjs.extend(duration);

const MFAForm = ({ mfa, error }) => {
  const [confirmed, setConfirmed] = useState();
  const [hasResent, setResent] = useState(false);
  const [nextEnableTime, setNextEnableTime] = useState();
  const [timeLeft, setTimeLeft] = useState(0);
  const [verificationError, setVerificationError] = useState();
  const isResending = useRef(false);
  const sessions = useRef([mfa.user]);
  const { AuthStore } = useRootStore();
  const { user, email, password } = mfa;
  const dest = user.challengeParam.CODE_DELIVERY_DESTINATION;
  const mobileNumber = `•••• ••• ${dest.slice(-3)}`;

  const resend = useCallback(async () => {
    if (isResending.current) {
      return;
    }

    setResent(false);
    if (!!nextEnableTime && dayjs().isBefore(nextEnableTime)) {
      return;
    }

    isResending.current = true;
    const { user: session } = await AuthStore.signIn({ email, password });
    sessions.current = concat(sessions.current, session);
    setResent(true);
    setNextEnableTime(dayjs().add('1', 'minute'));
    isResending.current = false;

    // @TODO: Rollbar is not the best place for this. However if we use analytics, none of them trigger notifications the moment.
    // Better solutions welcomed
    logger.warn('MFA resend clicked', { email });
  }, [AuthStore, email, nextEnableTime, password]);

  const onSubmit = useCallback(
    async ({ code }) => {
      analytics.track(ANALYTICS_EVENTS.USER_MULTI_FACTOR_AUTH_SUBMITTED);

      try {
        // @NOTE: we have to do this because of https://github.com/aws-amplify/amplify-js/issues/2010
        await reduceRight(
          sessions.current,
          async (previousPromise, session) => {
            try {
              await previousPromise;
              setConfirmed(true);
              return Promise.resolve();
            } catch (err) {
              // ignores the error and try another session
              return AuthStore.confirmSignIn({ code, user: session });
            }
          },
          Promise.reject(new Error('Initiate'))
        );

        analytics.track(ANALYTICS_EVENTS.USER_MULTI_FACTOR_AUTHORISED);
      } catch (err) {
        setVerificationError(err.message);
        analytics.track(ANALYTICS_EVENTS.USER_MULTI_FACTOR_AUTH_FAILED, { failureReason: err.message });
      }
    },
    [AuthStore]
  );

  useEffect(() => {
    const timer = setInterval(() => {
      if (!nextEnableTime) {
        clearInterval(timer);
        return;
      }
      const now = dayjs();
      if (now.isBefore(nextEnableTime)) {
        const seconds = Math.floor(dayjs.duration(nextEnableTime.diff(now)).asSeconds());
        if (seconds >= 0) {
          setTimeLeft(seconds);
        } else {
          setTimeLeft(0);
        }
      } else {
        clearInterval(timer);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [nextEnableTime]);

  return (
    <Form
      submitted={confirmed}
      initialValues={{ code: '' }}
      onSubmit={onSubmit}
      validationSchema={schemas.confirmSignIn}
      heading="Sign in to your account"
      error={error || verificationError}
      buttonText="Submit"
    >
      <Box mb={1}>
        <P variant={1} textAlign="center">
          A two factor authentication code was sent to your mobile number {mobileNumber}
        </P>
      </Box>
      <Box mb={1}>
        <FormikTextInput
          name="code"
          label="Verification code"
          maxLength={6}
          placeholder="Enter 6 digit code"
          autoComplete={INPUT_AUTOCOMPLETE.ONE_TIME_CODE}
        />
      </Box>
      <Box mb={1}>
        <P variant={3}>
          <A
            onClick={() => {
              analytics.track(ANALYTICS_EVENTS.USER_MULTI_FACTOR_AUTH_CODE_RESENT);
              resend();
            }}
          >
            Resend
          </A>
        </P>
      </Box>
      {timeLeft > 0 && !hasResent && (
        <Box mb={2}>
          <Alert type="info">You can resend the code in {timeLeft} seconds</Alert>
        </Box>
      )}
      {hasResent && (
        <Box mb={2}>
          <Alert type="success">Code resent to your mobile number</Alert>
        </Box>
      )}
    </Form>
  );
};

MFAForm.propTypes = {
  mfa: PropTypes.shape({
    user: PropTypes.shape({
      challengeParam: PropTypes.shape({
        CODE_DELIVERY_DESTINATION: PropTypes.string.isRequired,
      }).isRequired,
    }),
    email: PropTypes.string.isRequired,
    password: PropTypes.string.isRequired,
  }).isRequired,
  error: PropTypes.string,
};

MFAForm.defaultProps = {
  error: null,
};

const ComposedMFAForm = observer(MFAForm);
export { ComposedMFAForm as MFAForm };
